import{actionResourceVersionMatches,isScheduledGiftAction}from'./scheduled-gift-preparation';
import {
  ApiError,
  canonicalJSON,
  commandDigest,
  type Action,
  type CommandStatus,
  isNativeTransport,
  type Transport,
  type Field,
} from "./transport";

const prefix = "thistleglass.unresolved.";
const opaque = (key: string) => /^[A-Za-z0-9_-]{8,200}$/.test(key);
type PendingBinding = Pick<CommandStatus, "state" | "resource"> &
  Partial<Pick<CommandStatus, "operation" | "outcome">>;
// Original M05.03 outer ok means a valid execution observation, not necessarily
// a terminal refund. Preserve original states; never invent completion from 200.
const terminalRefund = (
  outcome: any,
  resource?: { id: string; version: number },
) => {
  const refund = outcome?.value?.refund;
  return (
    outcome?.kind === "ok" &&
    Number.isSafeInteger(outcome.version) &&
    outcome.version > 0 &&
    refund &&
    typeof refund === "object" &&
    !Array.isArray(refund) &&
    ["succeeded", "failed"].includes(refund.state) &&
    refund.version === outcome.version &&
    Number.isSafeInteger(refund.orderVersion) &&
    refund.orderVersion > 0 &&
    [
      "id",
      "orderId",
      "currency",
      "allocationDigest",
      "reasonCode",
      "attemptKey",
      "commandDigest",
      "recordedAt",
    ].every(
      (key) => typeof refund[key] === "string" && refund[key].trim().length > 0,
    ) &&
    Number.isFinite(Date.parse(refund.recordedAt)) &&
    refund.allocated &&
    refund.allocated.currency === refund.currency &&
    typeof refund.allocated.minor === "string" &&
    /^(0|[1-9][0-9]*)$/.test(refund.allocated.minor) &&
    (!resource ||
      (refund.orderId === resource.id &&
        refund.orderVersion === resource.version))
  );
};
const terminal = (status: PendingBinding) =>
  status.state === "committed" &&
  (status.operation !== "aftersales.refund" ||
    status.outcome?.kind === "denied" ||
    status.outcome?.kind === "conflict" ||
    terminalRefund(status.outcome, status.resource));
/** References survive reload; private bindings exist only in memory and are rediscovered under current server ACLs. */
const pendingTransportOwners=new WeakMap<object,Transport>();
export function pendingOwnsTransport(pending:unknown,api:unknown):boolean{return !!pending&&typeof pending==='object'&&isNativeTransport(api)&&pendingTransportOwners.get(pending)===api}
export class PendingCommands {
  private revision = 0;
  private listeners = new Set<() => void>();
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  snapshot = () => this.revision;
  private notify() {
    this.revision++;
    for (const listener of this.listeners) listener();
  }
  private records = new Map<string, PendingBinding | undefined>();
  // Current-epoch memory only. Persistent storage still contains opaque keys alone.
  private expected = new Map<
    string,
    Pick<
      CommandStatus,
      "operation" | "commandDigest" | "resource" | "instanceId"
    >
  >();
  private uploadExpected = new Map<
    string,
    Pick<CommandStatus, "operation" | "commandDigest" | "resource">
  >();
  private requireCurrentBinding(key: string, status: CommandStatus) {
    const expected = this.expected.get(key);
    if (
      expected &&
      (expected.operation !== status.operation ||
        expected.commandDigest !== status.commandDigest ||
        expected.instanceId !== status.instanceId ||
        expected.resource.id !== status.resource.id ||
        expected.resource.version !== status.resource.version)
    )
      throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key);
    const upload = this.uploadExpected.get(key);
    if (
      upload &&
      (upload.operation !== status.operation ||
        upload.commandDigest !== status.commandDigest ||
        upload.resource.id !== status.resource.id ||
        upload.resource.version !== status.resource.version)
    )
      throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key);
    // Upload facade derives its own server instance; the parent form instance
    // is not the identity of the original uploaded command.
  }
  private attempts = new Map<string, string>();
  private uploadAttempts = new Map<string, string>();
  private epoch = -1;
  private ready = false;
  private recovering?: Promise<void>;
  constructor(
    private api: Transport,
    private storage?: Storage,
  ) {
    pendingTransportOwners.set(this,api);
    try {
      for (let i = 0; storage && i < storage.length; i++) {
        const slot = storage.key(i)!;
        if (slot.startsWith(prefix)) {
          const key = storage.getItem(slot)!;
          if (opaque(key) && slot === prefix + key)
            this.records.set(key, undefined);
        }
      }
    } catch {
      /* Server enumeration is mandatory even when storage is unavailable. */
    }
  }
  private remember(key: string, value?: PendingBinding) {
    this.records.set(key, value);
    this.notify();
    try {
      this.storage?.setItem(prefix + key, key);
    } catch {
      /* server recovers references */
    }
  }
  private forget(key: string) {
    this.records.delete(key);
    this.expected.delete(key);
    this.uploadExpected.delete(key);
    this.notify();
    try {
      this.storage?.removeItem(prefix + key);
    } catch {
      /* keep conservative reference */
    }
  }
  scheduledGiftBlocked(){return Array.from(this.records).some(([key,record])=>!record||isScheduledGiftAction({id:(record.operation??this.expected.get(key)?.operation) as Action['id']}))}
  blocked(resourceId: string) {
    return Array.from(this.records.values()).some(
      (record) => !record || record.resource.id === resourceId,
    );
  }
  get references() {
    return Array.from(this.records.keys());
  }
  resetIdentity() {
    this.ready = false;
    this.recovering = undefined;
    this.attempts.clear();
    this.expected.clear();
    this.uploadExpected.clear();
    this.uploadAttempts.clear();
    for (const key of this.records.keys()) this.records.set(key, undefined);
    this.epoch = this.api.sessionEpoch;
    this.notify();
  }
  private checkEpoch(epoch: number) {
    if (epoch !== this.api.sessionEpoch)
      throw new ApiError("denied", "SESSION_CHANGED");
  }
  async recover(): Promise<void> {
    if (this.epoch !== this.api.sessionEpoch) this.resetIdentity();
    if (this.recovering) return this.recovering;
    const epoch = this.api.sessionEpoch;
    const run = async () => {
      this.ready = false;
      // Missing local storage cannot hide server-side unresolved operations.
      const cursors = new Set<string>();
      let cursor: string | undefined;
      do {
        const page = await this.api.unresolved(cursor);
        this.checkEpoch(epoch);
        for (const record of page.items) {
          if (!opaque(record.idempotencyKey))
            throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED");
          this.requireCurrentBinding(record.idempotencyKey, record);
          if (!terminal(record)) this.remember(record.idempotencyKey, record);
        }
        cursor = page.next;
        if (cursor && (cursors.has(cursor) || cursors.size >= 100))
          throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED");
        if (cursor) cursors.add(cursor);
      } while (cursor);
      for (const key of this.records.keys()) {
        try {
          const record = await this.api.commandStatus(key);
          this.checkEpoch(epoch);
          this.requireCurrentBinding(key, record);
          this.remember(key, record);
        } catch (e) {
          this.checkEpoch(epoch);
          // A denial may be a foreign reference or revoked access. It never authorizes a new effect.
          this.records.set(key, undefined);
          this.notify();
        }
      }
      this.ready = true;
    };
    const promise = run();
    this.recovering = promise;
    try {
      await promise;
    } finally {
      if (this.recovering === promise) this.recovering = undefined;
    }
  }
  async inspect(key: string) {
    const epoch = this.api.sessionEpoch;
    const record = await this.api.commandStatus(key);
    this.checkEpoch(epoch);
    this.requireCurrentBinding(key, record);
    if (terminal(record)) this.forget(key);
    else {
      this.remember(key, record);
      // A legacy server may label original reconciling/prepared/pending as
      // committed. Keep its opaque fence and refuse a misleading terminal view.
      if (
        record.operation === "aftersales.refund" &&
        record.state === "committed"
      )
        throw new ApiError("pending", "REFUND_STATUS_UNRESOLVED", key, false);
    }
    return record;
  }
  async upload(
    file: File,
    resource: { id: string; version: number },
    purpose: NonNullable<Field["uploadPurpose"]>,
    key: string,
    maxBytes?: number,
    instanceId?: string,
  ) {
    if (!file.size || file.size > (maxBytes ?? 10 * 1024 * 1024))
      throw new ApiError("error", "UPLOAD_SIZE_INVALID");
    const epoch = this.api.sessionEpoch;
    if (!this.ready || this.epoch !== epoch) await this.recover();
    this.checkEpoch(epoch);
    for (const [existing, record] of this.records) {
      if (!record || record.resource.id === resource.id)
        throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", existing);
    }
    const fileDigest = Array.from(
      new Uint8Array(
        await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
      ),
      (byte) => byte.toString(16).padStart(2, "0"),
    ).join("");
    this.checkEpoch(epoch);
    // Match the unchanged server upload-facade's original file-inclusive digest.
    const uploadCommandDigest = Array.from(
      new Uint8Array(
        await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(
            canonicalJSON({
              operation: "upload.prepare",
              expectedVersion: resource.version,
              input: { resource, purpose },
              fileSHA256: fileDigest,
            }),
          ),
        ),
      ),
      (byte) => byte.toString(16).padStart(2, "0"),
    ).join("");
    this.checkEpoch(epoch);
    const fingerprint = canonicalJSON({
      epoch,
      resource,
      purpose,
      instanceId: instanceId || "",
      fileDigest,
    });
    if (
      this.uploadAttempts.has(key) &&
      this.uploadAttempts.get(key) !== fingerprint
    )
      throw new ApiError("stale", "UPLOAD_IDENTITY_CHANGED");
    for (const [existing, record] of this.records) {
      if (!record || record.resource.id === resource.id)
        throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", existing);
    }
    this.uploadAttempts.set(key, fingerprint);
    this.uploadExpected.set(key, {
      operation: "upload.prepare",
      commandDigest: uploadCommandDigest,
      resource: { ...resource },
    });
    this.remember(key, { state: "pending", resource });
    try {
      const result = await this.api.upload(
        file,
        resource,
        purpose,
        key,
        maxBytes,
        (purpose === "listing" || purpose === "digital") ? instanceId : undefined,
      );
      this.checkEpoch(epoch);
      this.forget(key);
      return result;
    } catch (e) {
      this.checkEpoch(epoch);
      // A post-dispatch denial/conflict cannot establish that bytes were not stored.
      // Keep the original reference until current authoritative same-key recovery.
      throw e;
    }
  }
  async submit(
    action: Action,
    input: Record<string, unknown>,
    resourceId: string,
  ) {
    if (
      !actionResourceVersionMatches(action) || action.resource!.id !== resourceId
    )
      throw new ApiError("stale", "ACTION_RESOURCE_REQUIRED");
    if (this.epoch !== this.api.sessionEpoch) this.resetIdentity();
    if (!this.ready) await this.recover();
    const epoch = this.api.sessionEpoch;
    // Subject fencing deliberately ignores changed input/version/instance IDs.
    for (const [key, record] of this.records) {
      if (!record || record.resource.id === resourceId || isScheduledGiftAction(action)&&isScheduledGiftAction({id:(record.operation??this.expected.get(key)?.operation) as Action['id']}))
        throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key, false);
    }
    const digest = await commandDigest(
      action.id,
      action.expectedVersion,
      input,
    );
    this.checkEpoch(epoch);
    for (const [key, record] of this.records) {
      if (!record || record.resource.id === resourceId || isScheduledGiftAction(action)&&isScheduledGiftAction({id:(record.operation??this.expected.get(key)?.operation) as Action['id']}))
        throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key);
    }
    const fingerprint = canonicalJSON({
      epoch,
      resource: action.resource,
      instanceId: action.instanceId,
      expectedVersion: action.expectedVersion,
      digest,
    });
    let key = this.attempts.get(fingerprint);
    if (!key) {
      if (
        action.id === "aftersales.refund" ||
        action.id === "checkout.cancel"
      ) {
        // The trusted server prepared this exact original refund/cancellation digest using
        // its fresh opaque instance ID as the ORIGINAL command key. This ID
        // is correlation only; original proof/current server authority still
        // decides the effect. All preceding subject/epoch fences stay intact.
        const confirmation =
          action.immutableconfirmation ?? action.confirmation;
        if (
          !action.protected ||
          !Array.isArray(action.fields) ||
          action.fields.length ||
          !/^[A-Za-z0-9_-]{16,200}$/.test(action.instanceId) ||
          !action.expiresAt ||
          !Number.isFinite(Date.parse(action.expiresAt)) ||
          Date.parse(action.expiresAt) <= Date.now() ||
          !confirmation ||
          !confirmation.principal ||
          !/^[a-f0-9]{64}$/.test(confirmation.digest ?? "") ||
          confirmation.resource.id !== resourceId ||
          confirmation.resource.version !== action.expectedVersion ||
          (action.id === "aftersales.refund"
            ? !/^[a-f0-9]{64}$/.test(String(input.confirmationId ?? "")) ||
              !Number.isFinite(
                Date.parse(String(input.confirmationExpiresAt ?? "")),
              ) ||
              Date.parse(String(input.confirmationExpiresAt)) <= Date.now()
            : typeof input.humanProof !== "string" ||
              !input.humanProof.trim() ||
              input.freezeId !== resourceId) ||
          canonicalJSON(input) !== canonicalJSON(action.input)
        )
          throw new ApiError(
            "denied",
            action.id === "aftersales.refund"
              ? "INVALID_PREPARED_REFUND"
              : "INVALID_PREPARED_CANCELLATION",
          );
        key = action.instanceId;
      } else key = crypto.randomUUID();
      this.attempts.set(fingerprint, key);
    }
    this.expected.set(key, {
      operation: action.id,
      commandDigest: digest,
      resource: { ...action.resource },
      instanceId: action.instanceId,
    });
    this.remember(key, {
      state: "pending",
      resource: action.resource,
    });
    try {
      const outcome = await this.api.command(
        action.id,
        { idempotencyKey: key, expectedVersion: action.expectedVersion, input },
        action.instanceId,
      );
      this.checkEpoch(epoch);
      if (
        action.id === "aftersales.refund" &&
        !terminalRefund(outcome, action.resource)
      ) {
        this.remember(key, {
          state: "unknown",
          resource: action.resource,
          operation: "aftersales.refund",
        });
        throw new ApiError("pending", "REFUND_STATUS_UNRESOLVED", key, false);
      }
      this.forget(key);
      return outcome;
    } catch (e) {
      this.checkEpoch(epoch);
      // HTTP failure alone cannot prove no effect; authoritative lookup releases the fence.
      // Errors, including malformed replies, preserve the reference. No automatic resubmit.
      throw e;
    }
  }
}
